'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { ArrowLeft, ArrowRight, CircleCheckBig, MoveLeft, RotateCcw } from 'lucide-react'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import { navigateUrl } from '@/utils/client-request'
import { useForgotPasswordFlow } from '@/lib/commerce/hooks'

export default function Forgot({ vendor }: { vendor: any }) {
  const pathname = usePathname() || ''
  const f = useForgotPasswordFlow(vendor)
  const handleKeyDown = (event: any) => { if (event.key === 'Enter') f.sendOTP() }

  return (
    <div>
      <Link href={navigateUrl('/login', pathname)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground/60 hover:text-foreground transition-colors mb-5">
        <ArrowLeft className="size-3.5" /> Нэвтрэх рүү буцах
      </Link>

      <AnimatePresence mode="wait">
        {(f.step == 3 && f.sentOTP && f.verificationId) ? (
          <motion.div key="reset" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <h1 className="text-[36px] sm:text-[40px] font-bold font-gilroy tracking-[-0.025em] leading-[1.05] text-foreground">
              Шинэ нууц үг
            </h1>
            <p className="mt-2 text-[14px] text-foreground/55">Шинэ нууц үгээ оруулна уу.</p>
            <form onSubmit={f.handleResetPassword} className="mt-7 flex flex-col gap-4">
              <Input label="Шинэ нууц үг" type="password" onChange={(e: any) => f.setPassword(e.target.value)} placeholder="Таны шинэ нууц үг" description="8-аас дээш тэмдэгтэй байх ёстой" />
              <Input label="Шинэ нууц үг давтах" type="password" onChange={(e: any) => f.setPasswordRep(e.target.value)} placeholder="Шинэ нууц үг давтах" />
              <Button type="submit" isLoading={f.loading} icon={ArrowRight} iconPosition="right" variant="outline" className="mt-2 w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" loadingMessage="Шинэчилж байна...">
                Нууц үг хадгалах
              </Button>
            </form>
          </motion.div>
        ) : (f.step == 2 && f.sentOTP) ? (
          <motion.div key="otp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-3">Алхам 2 / 3</div>
            <h1 className="text-[36px] sm:text-[40px] font-bold font-gilroy tracking-[-0.025em] leading-[1.05] text-foreground">
              Кодоо оруулна уу
            </h1>
            <p className="mt-3 text-[15px] text-foreground/60 leading-[1.5]">
              <b className="text-foreground">{f.identifier}</b> хаяг руу 6 оронтой нэг удаагийн код илгээсэн.
            </p>

            <div className="flex justify-center mt-6">
              {/* @ts-ignore */}
              <InputOTP value={f.otp} onChange={f.setOTP} autoFocus={true} maxLength={6} pattern={REGEXP_ONLY_DIGITS} autoComplete="false">
                {/* @ts-ignore */}
                <InputOTPGroup>
                  {/* @ts-ignore */}
                  <InputOTPSlot index={0} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={1} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={2} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={3} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={4} showChar={true} />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={5} showChar={true} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button type="button" onClick={f.handleOTP} icon={CircleCheckBig} isLoading={f.isOTPLoading} variant="outline" className="w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" loadingMessage="Баталгаажуулж байна...">
                Баталгаажуулах
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" onClick={f.reset} icon={MoveLeft} variant="ghost" className="w-full h-11 rounded-full!">
                  Буцах
                </Button>
                <Button type="button" onClick={f.resendOTP} icon={RotateCcw} isLoading={f.resendLoading} variant="ghost" className="w-full h-11 rounded-full!" loadingMessage="Илгээж байна..." disabled={f.isCooldownActive}>
                  {f.isCooldownActive ? `${f.cooldownSeconds} секунд` : 'Дахин илгээх'}
                </Button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="ask" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <h1 className="text-[36px] sm:text-[40px] font-bold font-gilroy tracking-[-0.025em] leading-[1.05] text-foreground">
              Нууц үгээ<br />мартсан уу?
            </h1>
            <p className="mt-3 text-[15px] text-foreground/60 leading-[1.5]">
              Цахим шуудан эсвэл утасны дугаараа оруулна уу. Бид сэргээх кодыг шууд илгээх болно.
            </p>

            <Tabs value={f.tab} onValueChange={(v: any) => f.setTab(v)} className="w-full mt-6">
              <TabsList className="w-full">
                <TabsTrigger value="phone">Утасны дугаар</TabsTrigger>
                <TabsTrigger value="email">Имэйл хаяг</TabsTrigger>
              </TabsList>
              <TabsContent value="phone">
                <div className="flex flex-col gap-4 mt-3">
                  <Input value={f.phone} label="Утасны дугаар" type="text" placeholder="00000000" onChange={(e: any) => f.setPhone(e.target.value)} onKeyDown={handleKeyDown} />
                  <Button onClick={f.sendOTP} isLoading={f.sendOTPLoading} icon={ArrowRight} iconPosition="right" variant="outline" className="mt-2 w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" loadingMessage="Илгээж байна...">
                    Сэргээх код илгээх
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="email">
                <div className="flex flex-col gap-4 mt-3">
                  <Input value={f.email} label="Имэйл хаяг" type="email" placeholder="example@shop.mn" onChange={(e: any) => f.setEmail(e.target.value)} onKeyDown={handleKeyDown} />
                  <Button onClick={f.sendOTP} isLoading={f.sendOTPLoading} icon={ArrowRight} iconPosition="right" variant="outline" className="mt-2 w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all" loadingMessage="Илгээж байна...">
                    Сэргээх код илгээх
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <p className="mt-7 text-[14px] text-center text-foreground/65">
              Нууц үгээ санасан уу?{' '}
              <Link href={navigateUrl('/login', pathname)} className="cursor-pointer font-semibold text-foreground underline underline-offset-[3px] hover:text-foreground/80 transition-colors">
                Нэвтрэх
              </Link>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
